import { MlTextRecord } from 'src/app/Theme/shared/service/ml-description.service';
import { parseCategoryPageName } from './category-layout.util';

export const COMPARISON_COMPONENT_KEY = 'comparison' as const;
export const COMPARISON_PAGE_NAME_KEY = 'pageName';
/** Mobile page title. Falls back to desktop `pageName` only when this key is absent. */
export const COMPARISON_PAGE_NAME_MOBILE_KEY = 'pageNameMobile';
export const COMPARISON_MINIMUM_ITEMS_KEY = 'minimumItems';
export const COMPARISON_MAXIMUM_ITEMS_KEY = 'maximumItems';
/** Mobile maximum. Falls back to desktop `maximumItems` only when this key is absent. */
export const COMPARISON_MAXIMUM_ITEMS_MOBILE_KEY = 'maximumItemsMobile';

export type ComparisonLayoutDevice = 'desktop' | 'mobile';

/** Minimum products required to start a comparison. Not configurable. */
export const COMPARISON_MINIMUM_ITEMS = 2;
export const COMPARISON_MAXIMUM_CHOICES = [2, 3, 4, 5] as const;
export const COMPARISON_DEFAULT_MAXIMUM_ITEMS = 5;

export function comparisonPageNamePayloadKey(
  device: ComparisonLayoutDevice = 'desktop',
): string {
  return device === 'mobile' ? COMPARISON_PAGE_NAME_MOBILE_KEY : COMPARISON_PAGE_NAME_KEY;
}

export function comparisonMaximumItemsPayloadKey(
  device: ComparisonLayoutDevice = 'desktop',
): string {
  return device === 'mobile' ? COMPARISON_MAXIMUM_ITEMS_MOBILE_KEY : COMPARISON_MAXIMUM_ITEMS_KEY;
}

export function defaultComparisonPayload(): Record<string, unknown> {
  return {
    [COMPARISON_PAGE_NAME_KEY]: {},
    [COMPARISON_PAGE_NAME_MOBILE_KEY]: {},
    [COMPARISON_MINIMUM_ITEMS_KEY]: COMPARISON_MINIMUM_ITEMS,
    [COMPARISON_MAXIMUM_ITEMS_KEY]: COMPARISON_DEFAULT_MAXIMUM_ITEMS,
    [COMPARISON_MAXIMUM_ITEMS_MOBILE_KEY]: COMPARISON_DEFAULT_MAXIMUM_ITEMS,
  };
}

export function parseComparisonMaximumItems(
  payload: Record<string, unknown> | null | undefined,
): number {
  const raw = payload?.[COMPARISON_MAXIMUM_ITEMS_KEY];
  const value = typeof raw === 'number' ? raw : Number(raw);
  return (COMPARISON_MAXIMUM_CHOICES as readonly number[]).includes(value)
    ? value
    : COMPARISON_DEFAULT_MAXIMUM_ITEMS;
}

/**
 * Desktop reads `maximumItems`. Mobile reads `maximumItemsMobile` when that key
 * exists; otherwise it falls back to the desktop maximum.
 */
export function parseComparisonMaximumItemsForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: ComparisonLayoutDevice = 'desktop',
): number {
  if (
    device === 'mobile' &&
    payload &&
    Object.prototype.hasOwnProperty.call(payload, COMPARISON_MAXIMUM_ITEMS_MOBILE_KEY)
  ) {
    return parseComparisonMaximumItems({
      [COMPARISON_MAXIMUM_ITEMS_KEY]: payload[COMPARISON_MAXIMUM_ITEMS_MOBILE_KEY],
    });
  }
  return parseComparisonMaximumItems(payload);
}

export function parseComparisonPageName(
  payload: Record<string, unknown> | null | undefined,
  preferredLang?: string,
): string {
  return parseCategoryPageName(payload, preferredLang);
}

/** Device page title. Mobile uses `pageNameMobile` when present, else desktop `pageName`. */
export function parseComparisonPageNameForDevice(
  payload: Record<string, unknown> | null | undefined,
  preferredLang?: string,
  device: ComparisonLayoutDevice = 'desktop',
): string {
  const record = parseComparisonPageNameRecordForDevice(payload, device);
  return parseComparisonPageName({ [COMPARISON_PAGE_NAME_KEY]: record }, preferredLang);
}

export function parseComparisonPageNameRecord(
  payload: Record<string, unknown> | null | undefined,
  key: string = COMPARISON_PAGE_NAME_KEY,
): MlTextRecord {
  const value = payload?.[key];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? { en: trimmed } : {};
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const record: MlTextRecord = {};
  for (const [lang, text] of Object.entries(value as Record<string, unknown>)) {
    if (typeof text === 'string' && text.trim()) {
      record[lang] = text.trim();
    }
  }
  return record;
}

/**
 * Desktop reads `pageName`. Mobile reads `pageNameMobile` when that key exists;
 * otherwise it falls back to the desktop page name.
 */
export function parseComparisonPageNameRecordForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: ComparisonLayoutDevice = 'desktop',
): MlTextRecord {
  if (
    device === 'mobile' &&
    payload &&
    Object.prototype.hasOwnProperty.call(payload, COMPARISON_PAGE_NAME_MOBILE_KEY)
  ) {
    return parseComparisonPageNameRecord(payload, COMPARISON_PAGE_NAME_MOBILE_KEY);
  }
  return parseComparisonPageNameRecord(payload);
}
